/* eslint-disable react/jsx-no-duplicate-props */
/**
 * Invited user sign-up using backend OTP flow.
 * Email comes from invite token (read-only). User requests OTP (sent via ACS), enters OTP + password,
 * backend creates user in Entra via Graph API. Works when Entra is configured for EmailOtpSignup
 * (no password support in native auth).
 * @see ROLES_AND_INVITES.md - Custom sign-up page (OTP + password)
 */
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { protectedResources } from "authConfig";

const apiBase = protectedResources?.apiTodoList?.endpoint || process.env.REACT_APP_API_URL || "";

const INVITE_BY_TOKEN = `${apiBase}/users/invite-by-token`;
const SEND_SIGNUP_OTP = `${apiBase}/users/send-signup-otp`;
const COMPLETE_SIGNUP = `${apiBase}/users/complete-signup`;

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(!!inviteToken);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("request"); // "request" | "verify"
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!inviteToken || !apiBase) {
      setLoading(false);
      if (!inviteToken) setError("Missing invite link. Use the link from your invitation email.");
      return;
    }
    fetch(`${INVITE_BY_TOKEN}?token=${encodeURIComponent(inviteToken)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.email) {
          setInvite({ email: data.email, role: data.role, tenantId: data.tenantId });
          setStep("request");
        } else {
          setError(data.error || "Invite not found or expired.");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load invite. Please try the link from your email again.");
        setLoading(false);
      });
  }, [inviteToken]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!inviteToken) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(SEND_SIGNUP_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: inviteToken }),
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        setStep("verify");
      } else {
        setError(data.error || "Could not send verification code. Try again.");
      }
    } catch {
      setError("Could not send verification code. Try again.");
    }
    setSubmitting(false);
  };

  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    if (!inviteToken) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must include at least one uppercase letter.");
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError("Password must include at least one number.");
      return;
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      setError("Password must include at least one special character (e.g. !, @, #, $).");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!otp.trim()) {
      setError("Enter the verification code from your email.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(COMPLETE_SIGNUP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: inviteToken, otp: otp.trim(), password }),
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        setStep("success");
      } else {
        setError(data.error || "Could not create account. The code may have expired.");
      }
    } catch {
      setError("Could not create account. Try again.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </MDBox>
    );
  }

  if (error && !invite) {
    return (
      <MDBox pt={4} px={2} maxWidth={480} mx="auto">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <MDButton variant="gradient" color="info" onClick={() => navigate("/")}>
          Back to sign in
        </MDButton>
      </MDBox>
    );
  }

  if (step === "success") {
    return (
      <MDBox pt={4} px={2} maxWidth={480} mx="auto">
        <Alert severity="success" sx={{ mb: 2 }}>
          Your account has been created. Sign in with your email and password.
        </Alert>
        <MDButton variant="gradient" color="info" onClick={() => navigate("/")} fullWidth>
          Sign in
        </MDButton>
      </MDBox>
    );
  }

  if (step === "verify") {
    return (
      <MDBox pt={4} px={2} maxWidth={480} mx="auto">
        <Typography variant="h5" fontWeight="medium" gutterBottom>
          Create your account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We sent a one-time code to {invite?.email}. Enter it below along with your password.
        </Typography>
        <Box component="form" onSubmit={handleCompleteSignup}>
          <TextField
            fullWidth
            label="Email"
            value={invite?.email || ""}
            disabled
            margin="normal"
            helperText="Email from your invite (cannot be changed)"
          />
          <TextField
            fullWidth
            label="Verification code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
            margin="normal"
            placeholder="6-digit code"
            inputProps={{ maxLength: 8, inputMode: "numeric", autoComplete: "one-time-code" }}
            required
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            // ID108 — "new-password" stops the browser/password-manager from autofilling a saved
            // password (e.g. "Testpass1") into this brand-new-account field.
            inputProps={{ minLength: 8, autoComplete: "new-password" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {password.length > 0 && (
            <Box
              sx={{
                mt: 0.5,
                mb: 0.5,
                px: 1.5,
                py: 1,
                bgcolor: "#f9f9f9",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              {[
                { label: "At least 8 characters", met: password.length >= 8 },
                { label: "One uppercase letter", met: /[A-Z]/.test(password) },
                { label: "One number", met: /[0-9]/.test(password) },
                {
                  label: "One special character (e.g. !, @, #, $)",
                  met: /[^a-zA-Z0-9]/.test(password),
                },
              ].map(({ label, met }) => (
                <Box
                  key={label}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: met ? "#2e7d32" : "#bdbdbd",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: met ? "#2e7d32" : "#757575", lineHeight: 1.4 }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <TextField
            fullWidth
            label="Confirm password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            inputProps={{ autoComplete: "new-password" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 3 }}>
            <MDButton type="submit" variant="gradient" color="info" disabled={submitting} fullWidth>
              {submitting ? "Creating account…" : "Create account"}
            </MDButton>
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 2 }}
            color="text.secondary"
            component="button"
            type="button"
            onClick={() => {
              setStep("request");
              setOtp("");
              setError(null);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Request a new code
          </Typography>
        </Box>
      </MDBox>
    );
  }

  // Step: request OTP
  return (
    <MDBox pt={4} px={2} maxWidth={480} mx="auto">
      <Typography variant="h5" fontWeight="medium" gutterBottom>
        Create your account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your email is set from your invite. We&apos;ll send a one-time code to verify it, then
        you&apos;ll set your password.
      </Typography>
      <Box component="form" onSubmit={handleSendOtp}>
        <TextField
          fullWidth
          label="Email"
          value={invite?.email || ""}
          disabled
          margin="normal"
          helperText="Email from your invite (cannot be changed)"
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 3 }}>
          <MDButton type="submit" variant="gradient" color="info" disabled={submitting} fullWidth>
            {submitting ? "Sending code…" : "Send verification code"}
          </MDButton>
        </Box>
      </Box>
      <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
        A one-time code will be sent to your email. Check your inbox (and spam folder).
      </Typography>
    </MDBox>
  );
}
